import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatDateTime, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Loader, Button, EmptyState, Textarea, Select, Pill } from "@/components/ui/Primitives";
import { ArrowLeft, Wrench, Send, Sparkles, AlertTriangle, Clock, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { StatusPill, PriorityPill } from "@/pages/Tickets";

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [triage, setTriage] = useState(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [triageError, setTriageError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setTicket(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  // Restore cached triage for this ticket (saves LLM calls + budget).
  useEffect(() => {
    if (!id) return;
    try {
      const cached = localStorage.getItem(`crc_triage_${id}`);
      if (cached) setTriage(JSON.parse(cached));
    } catch {}
  }, [id]);

  const runTriage = async () => {
    if (!ticket) return;
    setTriageError(null);
    setTriageLoading(true);
    try {
      const { data } = await api.post("/smart/maintenance/triage", {
        description: `${ticket.title}\n\n${ticket.description || ""}`.trim(),
        property_label: ticket.property_label || ticket.unit_label || null,
        unit_label: ticket.unit_label || null,
        tenant_name: ticket.tenant_name || null,
        reported_via: "web",
      });
      const result = data?.data || data;
      setTriage(result);
      try { localStorage.setItem(`crc_triage_${id}`, JSON.stringify(result)); } catch {}
      toast.success("Smart triage complete");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not run smart triage";
      setTriageError(msg);
      toast.error(msg);
    } finally {
      setTriageLoading(false);
    }
  };

  const useTriageReply = () => {
    if (!triage?.tenant_reply_draft) return;
    setBody(triage.tenant_reply_draft);
    toast.success("Draft reply loaded — review and click Send");
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/tickets/${id}/messages`, { body: body.trim() });
      setBody("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    } finally { setSending(false); }
  };

  const updateStatus = async (status) => {
    try {
      await api.patch(`/tickets/${id}/status`, { status });
      toast.success(`Ticket marked ${status.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    }
  };

  if (loading) return <Loader/>;
  if (!ticket) return <EmptyState icon={Wrench} title="Ticket not found"/>;

  const isLandlord = user?.role === "landlord";
  const messages = ticket.messages || [];

  return (
    <div>
      <Link to="/tickets" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-tickets">
        <ArrowLeft className="w-4 h-4"/> Back to tickets
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                  <PriorityPill p={ticket.priority}/> · <StatusPill s={ticket.status}/>
                </div>
                <h1 className="font-display text-2xl md:text-3xl font-bold mt-3 text-slate-950">{ticket.title}</h1>
                <p className="mt-3 text-slate-700 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 mt-5">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Conversation</h2>
            {messages.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8">No messages yet. Start the conversation below.</div>
            ) : (
              <div className="space-y-4 mb-5">
                {messages.map((m, i) => {
                  const own = m.sender_id === user?.user_id;
                  return (
                    <div key={i} className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid={`message-${i}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${own ? "bg-green-800 text-white" : "bg-slate-100 text-slate-900"}`}>
                        <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${own ? "text-white/70" : "text-slate-500"}`}>
                          {m.sender_name || (own ? "You" : "Them")} · {formatDateTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <form onSubmit={send} className="flex flex-col sm:flex-row gap-2">
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write a message…"
                className="flex-1 min-h-[64px]"
                testid="message-input"
              />
              <Button type="submit" disabled={sending || !body.trim()} className="sm:self-end" data-testid="send-message-button">
                <Send className="w-4 h-4"/> Send
              </Button>
            </form>
          </Card>
        </div>

        <div>
          <Card className="p-6 sticky top-6">
            <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Details</h3>
            <p className="text-xs text-slate-500 mb-4">Submitted {formatDate(ticket.created_at)}</p>
            <dl className="space-y-3 text-sm">
              <Row label="Status"><StatusPill s={ticket.status}/></Row>
              <Row label="Priority"><PriorityPill p={ticket.priority}/></Row>
              <Row label="Unit">{ticket.unit_id ? ticket.unit_id.slice(-8) : "—"}</Row>
            </dl>

            {isLandlord && (
              <div className="mt-6 pt-5 border-t border-slate-100 space-y-2">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Update status</div>
                <Select value={ticket.status} onChange={(e) => updateStatus(e.target.value)} testid="ticket-status-update">
                  <option value="open">Open</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In progress</option>
                  <option value="resolved">Resolved</option>
                </Select>
              </div>
            )}

            {isLandlord && <VendorAssignBlock ticket={ticket} onAssigned={load} />}
          </Card>

          {isLandlord && (
            <Card className="p-6 mt-5 border-emerald-100 bg-gradient-to-br from-emerald-50/40 to-white" data-testid="smart-triage-panel">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                    <Sparkles className="w-4 h-4"/>
                  </div>
                  <div>
                    <h3 className="font-display text-base font-bold text-slate-900">Smart Triage</h3>
                    <p className="text-[11px] text-slate-500">Smart severity + draft reply</p>
                  </div>
                </div>
                <Button
                  variant={triage ? "ghost" : "primary"}
                  onClick={runTriage}
                  disabled={triageLoading}
                  className="!px-3 !py-2 !text-xs"
                  data-testid="smart-triage-run"
                >
                  {triageLoading ? "Analyzing…" : triage ? "Re-run" : "Run smart triage"}
                </Button>
              </div>

              {triageError && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-3" data-testid="smart-triage-error">
                  {triageError}
                </div>
              )}

              {!triage && !triageLoading && !triageError && (
                <p className="text-xs text-slate-500" data-testid="smart-triage-empty">
                  Run smart triage to get severity, recommended vendor type, SLA, and a draft tenant reply you can send with one click.
                </p>
              )}

              {triage && (
                <div className="space-y-3" data-testid="smart-triage-result">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill tone={triage.severity === "emergency" ? "red" : triage.severity === "urgent" ? "amber" : triage.severity === "standard" ? "blue" : "slate"}>
                      <AlertTriangle className="w-3 h-3"/> {String(triage.severity || "").toUpperCase()}
                    </Pill>
                    {triage.category && <Pill tone="slate">{triage.category}</Pill>}
                    {typeof triage.response_sla_hours === "number" && (
                      <Pill tone="blue"><Clock className="w-3 h-3"/> SLA {triage.response_sla_hours}h</Pill>
                    )}
                    {(triage.estimated_cost_cad_low != null || triage.estimated_cost_cad_high != null) && (
                      <Pill tone="green">
                        <DollarSign className="w-3 h-3"/> ${triage.estimated_cost_cad_low ?? "?"}–${triage.estimated_cost_cad_high ?? "?"}
                      </Pill>
                    )}
                  </div>

                  {triage.recommended_vendor_type && (
                    <div className="text-xs">
                      <span className="text-slate-500">Suggested vendor: </span>
                      <span className="font-semibold text-slate-900">{triage.recommended_vendor_type}</span>
                    </div>
                  )}

                  {triage.landlord_summary && (
                    <div className="text-xs text-slate-700 bg-white border border-slate-200 rounded-lg p-3 leading-relaxed">
                      {triage.landlord_summary}
                    </div>
                  )}

                  {Array.isArray(triage.follow_up_questions) && triage.follow_up_questions.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Ask the tenant</div>
                      <ul className="text-xs text-slate-700 space-y-1 list-disc list-inside">
                        {triage.follow_up_questions.slice(0, 3).map((q, i) => <li key={i}>{q}</li>)}
                      </ul>
                    </div>
                  )}

                  {triage.tenant_reply_draft && (
                    <div className="pt-3 border-t border-emerald-100">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-1.5">Suggested tenant reply</div>
                      <div className="text-xs text-slate-700 bg-white border border-emerald-200 rounded-lg p-3 leading-relaxed whitespace-pre-wrap">
                        {triage.tenant_reply_draft}
                      </div>
                      <Button
                        variant="primary"
                        className="mt-3 w-full !py-2 !text-xs"
                        onClick={useTriageReply}
                        data-testid="smart-triage-use-reply"
                      >
                        <Send className="w-3.5 h-3.5"/> Use this reply
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900 font-medium">{children}</dd>
    </div>
  );
}

function VendorAssignBlock({ ticket, onAssigned }) {
  const [vendors, setVendors] = useState([]);
  const [pick, setPick] = useState(ticket?.assigned_vendor_id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/my-vendors")
      .then((r) => setVendors(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setVendors([]));
  }, []);

  const assign = async () => {
    setSaving(true);
    try {
      await api.patch(`/tickets/${ticket.ticket_id}`, { assigned_vendor_id: pick || null });
      const v = vendors.find((x) => (x.vendor_id || x.id) === pick);
      toast.success(v?.email ? `Assigned. Email sent to ${v.email}.` : "Vendor assigned.");
      onAssigned && onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign");
    } finally {
      setSaving(false);
    }
  };

  const current = vendors.find((v) => (v.vendor_id || v.id) === ticket?.assigned_vendor_id);

  return (
    <div className="mt-6 pt-5 border-t border-slate-100 space-y-2" data-testid="vendor-assign-block">
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Assign vendor</div>
      {current && (
        <div className="text-xs text-slate-600 mb-1">
          Currently: <strong>{current.name}</strong>{current.email ? ` · ${current.email}` : ""}
        </div>
      )}
      <select
        value={pick}
        onChange={(e) => setPick(e.target.value)}
        data-testid="vendor-assign-select"
        className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
      >
        <option value="">— Not assigned —</option>
        {vendors.map((v) => (
          <option key={v.vendor_id || v.id} value={v.vendor_id || v.id}>
            {v.name}{v.trade ? ` (${v.trade})` : ""}{v.email ? ` · ${v.email}` : ""}
          </option>
        ))}
      </select>
      <button
        onClick={assign}
        disabled={saving || pick === (ticket?.assigned_vendor_id || "")}
        data-testid="vendor-assign-submit"
        className="w-full px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
      >
        {saving ? "Assigning…" : pick ? "Assign & email vendor" : "Save"}
      </button>
      {vendors.length === 0 && (
        <div className="text-[11px] text-slate-500">
          No vendors saved yet. <Link to="/vendors" className="text-emerald-700 hover:underline">Add a vendor</Link> first.
        </div>
      )}
    </div>
  );
}

